import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LlmSettingsPanel from '@/components/settings/LlmSettingsPanel'
import AgentAccessPanel from '@/components/settings/AgentAccessPanel'
import { getSettings, saveSettings } from '@/api/settings'
import { testLlmKey, testDbConnection, getScmSettings, saveScmSettings, testScmConnection } from '@/api/workbench'
import { checkAgentAvailability } from '@/api/claudeagent'
import { changePassword, getRole, updateShortUserId } from '@/api/users'
import { useAuth } from '@/auth/AuthContext'
import { useProjectStore } from '@/store/projectStore'
import type { UserSettings } from '@/types'
import toast from 'react-hot-toast'
import chatgptIcon from '@/assets/icons/chatgpt-icon.svg'
import geminiIcon from '@/assets/icons/google-gemini-icon.svg'
import claudeIcon from '@/assets/icons/claude-ai-icon.svg'
import BillingProfileCard from '@/pages/billing/BillingProfileCard'
import TwoFactorCard from '@/components/settings/TwoFactorCard'

type SettingsTab = 'general' | 'scm' | 'agent-access' | 'account'

const LLM_PROVIDERS = [
  { value: 'openai', label: 'OpenAI', icon: chatgptIcon },
  { value: 'anthropic', label: 'Anthropic (Claude)', icon: claudeIcon },
  { value: 'google', label: 'Google (Gemini)', icon: geminiIcon },
]

const MODELS_BY_PROVIDER: Record<string, { value: string; label: string }[]> = {
  openai: [
    { value: 'gpt-6-astra', label: 'GPT-6 Astra (Latest)' },
    { value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol' },
    { value: 'gpt-6-luna', label: 'GPT-6 Luna' },
    { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol' },
    { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
    { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
    { value: 'gpt-5.5', label: 'GPT-5.5' },
    { value: 'gpt-5.4', label: 'GPT-5.4' },
    { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini' },
    { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano' },
    { value: 'gpt-5.2', label: 'GPT-5.2' },
    { value: 'gpt-5.1', label: 'GPT-5.1' },
    { value: 'gpt-5', label: 'GPT-5' },
    { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
    { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
    { value: 'o3', label: 'o3' },
    { value: 'o3-mini', label: 'o3 Mini' },
    { value: 'o4-mini', label: 'o4 Mini' },
    { value: 'gpt-4o', label: 'GPT-4o (Legacy)' },
  ],
  anthropic: [
    { value: 'claude-fable-5-1', label: 'Claude Fable 5.1' },
    { value: 'claude-fable-5', label: 'Claude Fable 5' },
    { value: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
    { value: 'claude-opus-5', label: 'Claude Opus 5' },
    { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
    { value: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
    { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
    { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
    { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  ],
  google: [
    // Google 404s the 2.5 ids for API projects created after their cut-off
    // ("no longer available to new users"), so the 3.x models lead the list.
    { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash (Latest)' },
    { value: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash' },
    { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
    { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
    { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite' },
    { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' },
    { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (Preview)' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (Legacy)' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Legacy)' },
    { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (Legacy)' },
  ],
}

// Models exposed by Claude Code (the CLI authenticates as the user's Claude
// subscription; only Claude models are supported on this backend).
const CLAUDE_CODE_MODELS: { value: string; label: string }[] = [
  { value: 'claude-fable-5-1', label: 'Claude Fable 5.1' },
  { value: 'claude-fable-5', label: 'Claude Fable 5' },
  { value: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
  { value: 'claude-opus-5', label: 'Claude Opus 5' },
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
  { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
]

// Backends available per task. "llm" routes through llm_service with the
// task's selected provider/model; "claude_code" runs the Claude Code CLI
// authenticated as the user's own subscription (via claude_oauth_token).
const TASK_BACKENDS: { value: 'llm' | 'claude_code'; label: string }[] = [
  { value: 'llm', label: 'LLM API' },
  { value: 'claude_code', label: 'Claude Code' },
]

const LLM_PROVIDER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Inherit default' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google' },
]

// The 10 generation tasks that can be individually routed. Order matches
// the backend's TASK_TYPES list.
const TASK_ROWS: { key: string; label: string }[] = [
  { key: 'frs', label: 'FRS Generation' },
  { key: 'enhance_frs', label: 'FRS Enhancement' },
  { key: 'enhance_spec', label: 'Spec Enhancement' },
  { key: 'app_json', label: 'Application Structure' },
  { key: 'model', label: 'Model Generation' },
  { key: 'business_logic', label: 'Business Logic (code)' },
  { key: 'page', label: 'Page Generation' },
  { key: 'visual', label: 'Visual Generation' },
  { key: 'schema', label: 'Schema Generation' },
  { key: 'dashboard', label: 'Dashboard Generation' },
  { key: 'report', label: 'Report Generation' },
  { key: 'project_modify', label: 'Project Modify' },
]

type TaskCfgEntry = { backend: 'llm' | 'claude_code'; provider: string; model: string; effort: string }

const DEFAULT_TASK_EFFORTS: Record<string, string> = {
  frs: 'high', enhance_frs: 'medium', enhance_spec: 'medium',
  app_json: 'low', model: 'low', business_logic: 'low', page: 'low',
  visual: 'low', schema: 'low', dashboard: 'low', report: 'low',
  project_modify: 'medium',
}

function defaultTaskEntry(taskKey: string): TaskCfgEntry {
  return { backend: 'llm', provider: '', model: '', effort: DEFAULT_TASK_EFFORTS[taskKey] || 'medium' }
}

const TARGET_TO_DB: Record<string, string> = {
  twasta_cloud: 'oracle',
  oracle_database: 'oracle',
  postgresql_database: 'postgresql',
  mysql_database: 'mysql',
}

const DEFAULT_PORTS: Record<string, string> = {
  oracle: '1521',
  postgresql: '5432',
  mysql: '3306',
}

interface ScmSettings {
  scm_enabled: boolean
  scm_provider: string
  auto_create_repo: boolean
  github_username: string
  github_token: string
  github_default_branch: string
  gitlab_url: string
  gitlab_username: string
  gitlab_token: string
  gitlab_default_branch: string
}

const defaultScmSettings: ScmSettings = {
  scm_enabled: false,
  scm_provider: 'github',
  auto_create_repo: false,
  github_username: '',
  github_token: '',
  github_default_branch: 'main',
  gitlab_url: 'https://gitlab.com',
  gitlab_username: '',
  gitlab_token: '',
  gitlab_default_branch: 'main',
}

const SETTINGS_TABS: SettingsTab[] = [
  'general', 'scm', 'agent-access', 'account',
]

export default function SettingsPage() {
  const navigate = useNavigate()
  // ?tab=agent-access deep-links a tab. `twasta login` prints exactly that URL
  // for the human to open, so the approval screen must be one click away rather
  // than "Settings, then find the right tab".
  const [searchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab') as SettingsTab | null
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    requestedTab && SETTINGS_TABS.includes(requestedTab) ? requestedTab : 'general',
  )

  // Usage moved to Billing, where the credits it is spent in already live.
  // Old ?tab=usage links (and anyone's bookmark) land there rather than on a
  // tab that no longer exists.
  useEffect(() => {
    if (requestedTab === ('usage' as SettingsTab)) {
      navigate('/billing?tab=usage', { replace: true })
    }
  }, [requestedTab, navigate])
  const [settings, setSettings] = useState<Partial<UserSettings>>({
    llm_provider: '',
    api_key: '',
    api_key_storage: 'save_securely',
    deployment_target: 'twasta_cloud',
    server_url: '',
    language_model: null,
    application_database: 'oracle',
    db_host: '',
    db_port: '',
    db_username: '',
    db_password: '',
    db_name: '',
    generation_mode: 'llm',
    thinking_effort_config: {
      frs: 'high',
      app_json: 'low',
      model: 'low',
      enhance_spec: 'medium',
      enhance_frs: 'medium',
    },
    openai_api_key: '',
    anthropic_api_key: '',
    google_api_key: '',
    claude_oauth_token: '',
    task_generation_config: Object.fromEntries(
      TASK_ROWS.map(({ key }) => [key, defaultTaskEntry(key)]),
    ),
  })
  // Subscription plans: F=Free, P=Pro, S=Saas
  const isFreePlan = !settings.subscription_plan || settings.subscription_plan === 'F'
  const [testingAgent, setTestingAgent] = useState(false)
  const [scmSettings, setScmSettings] = useState<ScmSettings>(defaultScmSettings)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingScm, setSavingScm] = useState(false)
  const [testingKey, setTestingKey] = useState(false)
  // Per-provider Test-key spinner state for the Provider Keys section.
  const [testingProviderKey, setTestingProviderKey] = useState<string | null>(null)
  // Per-credential UI scope: 'tenant' (shared default) or 'user' (personal
  // override). Initialised once from settings.user_credentials after load —
  // if the user already has a stored personal value, that row defaults to
  // the 'user' tab; otherwise 'tenant'.
  const [keyScope, setKeyScope] = useState<Record<string, 'tenant' | 'user'>>({})
  const [scopeInitialised, setScopeInitialised] = useState(false)
  const [testingScm, setTestingScm] = useState(false)
  const [testingDb, setTestingDb] = useState(false)
  const [roleDisplay, setRoleDisplay] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const { user, refreshUser } = useAuth()
  const [shortUserIdInput, setShortUserIdInput] = useState('')
  const [savingShortUserId, setSavingShortUserId] = useState(false)

  useEffect(() => {
    setShortUserIdInput(user?.short_user_id || '')
  }, [user?.short_user_id])

  useEffect(() => {
    loadAllSettings()
    getRole().then((r) => setRoleDisplay(r.role_display || r.role || '')).catch(() => {})
  }, [])

  const loadAllSettings = async () => {
    setLoading(true)
    try {
      const [settingsData, scmResponse] = await Promise.all([
        getSettings(),
        getScmSettings().catch(() => ({})),
      ])
      setSettings(settingsData)
      // Seed scope toggles from the stored personal overrides (only once per
      // load — user toggles after this point are preserved).
      if (!scopeInitialised) {
        const uc = (settingsData?.user_credentials || {}) as Record<string, string>
        setKeyScope({
          openai_api_key: uc.openai_api_key ? 'user' : 'tenant',
          anthropic_api_key: uc.anthropic_api_key ? 'user' : 'tenant',
          google_api_key: uc.google_api_key ? 'user' : 'tenant',
          claude_oauth_token: uc.claude_oauth_token ? 'user' : 'tenant',
        })
        setScopeInitialised(true)
      }
      // Backend returns { settings: { ... } } - unwrap it
      const scmData = scmResponse?.settings || scmResponse || {}
      if (scmData && typeof scmData === 'object') {
        setScmSettings({
          ...defaultScmSettings,
          ...scmData,
          // Map backend field name to frontend field name
          auto_create_repo: scmData.scm_auto_create_repo_on_draft_save ?? scmData.auto_create_repo ?? defaultScmSettings.auto_create_repo,
        })
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to load settings'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Top-level credential fields are the effective (resolved) values from
      // get_settings — sending them back would route them to tenant. Strip
      // them so only the explicit tenant_credentials / user_credentials
      // sub-dicts decide where credentials land on the backend.
      const {
        openai_api_key: _o,
        anthropic_api_key: _a,
        google_api_key: _g,
        claude_oauth_token: _c,
        ...payload
      } = settings
      void _o; void _a; void _g; void _c
      await saveSettings(payload)
      // Let the persistent Ask-AI dock (Sidebar) re-resolve its backend/model
      // instead of holding the values it read when it first mounted.
      window.dispatchEvent(new CustomEvent('twasta:settings-changed'))
      toast.success('Settings saved successfully')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save settings'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveScm = async () => {
    setSavingScm(true)
    try {
      // Map frontend field names to backend field names
      const payload = {
        scm_enabled: scmSettings.scm_enabled,
        scm_provider: scmSettings.scm_provider,
        scm_auto_create_repo_on_draft_save: scmSettings.auto_create_repo,
        github_username: scmSettings.github_username,
        github_token: scmSettings.github_token,
        github_default_branch: scmSettings.github_default_branch,
        gitlab_url: scmSettings.gitlab_url,
        gitlab_username: scmSettings.gitlab_username,
        gitlab_token: scmSettings.gitlab_token,
        gitlab_default_branch: scmSettings.gitlab_default_branch,
      }
      await saveScmSettings(payload)
      toast.success('SCM settings saved successfully')
      // The workbench overview caches the open project's SCM status and
      // re-selecting the same project does not refetch it, so refresh it
      // here or the overview keeps saying source control is not enabled.
      const { currentProject, fetchScmInfo } = useProjectStore.getState()
      const folder = currentProject?.application?.folder_name
      if (folder) void fetchScmInfo(folder)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save SCM settings'
      toast.error(msg)
    } finally {
      setSavingScm(false)
    }
  }

  const handleTestKey = async () => {
    if (!settings.llm_provider) {
      toast.error('Select a provider first')
      return
    }
    if (settings.llm_provider !== 'default' && !settings.api_key) {
      toast.error('Enter an API key first')
      return
    }
    setTestingKey(true)
    try {
      await testLlmKey(settings.llm_provider, settings.api_key || '')
      toast.success('API key is valid!')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'API key validation failed'
      toast.error(msg)
    } finally {
      setTestingKey(false)
    }
  }

  const handleTestScmConnection = async () => {
    setTestingScm(true)
    try {
      const provider = scmSettings.scm_provider
      const connectionData: Record<string, unknown> = { provider }
      if (provider === 'github') {
        connectionData.username = scmSettings.github_username
        connectionData.token = scmSettings.github_token
      } else {
        connectionData.url = scmSettings.gitlab_url
        connectionData.username = scmSettings.gitlab_username
        connectionData.token = scmSettings.gitlab_token
      }
      // The endpoint reports a REJECTED credential as HTTP 200 with
      // {success: false, error}, so awaiting the call is not a verdict —
      // resolving only means the request was delivered. Toasting success on
      // that let a wrong username or a token belonging to somebody else sit in
      // Settings looking healthy, which is exactly the confusion the backend's
      // three separate error messages exist to prevent.
      const result = await testScmConnection(connectionData)
      if (result?.success === false) {
        toast.error(result.error || 'Connection test failed')
        return
      }
      toast.success(result?.message || 'Connection successful!')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Connection test failed'
      toast.error(msg)
    } finally {
      setTestingScm(false)
    }
  }

  const handleTestClaudeCode = async () => {
    setTestingAgent(true)
    try {
      const result = await checkAgentAvailability()
      if (result.available) {
        toast.success(`Claude Code CLI is available: ${result.claude_path}`)
      } else {
        toast.error(result.message || 'Claude Code CLI is not available')
      }
    } catch {
      toast.error('Failed to check Claude Code availability')
    } finally {
      setTestingAgent(false)
    }
  }

  const handleTestDbConnection = async () => {
    const dbType = settings.application_database || 'oracle'
    if (!settings.db_host) {
      toast.error('Enter a host first')
      return
    }
    if (!settings.db_name) {
      toast.error('Enter a database/service name first')
      return
    }
    setTestingDb(true)
    try {
      const result = await testDbConnection({
        db_type: dbType,
        host: settings.db_host || '',
        port: settings.db_port || '',
        username: settings.db_username || '',
        password: settings.db_password || '',
        database: settings.db_name || '',
      })
      if (result.success) {
        toast.success(result.message || 'Connection successful!')
      } else {
        toast.error(result.error || 'Connection failed')
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Connection test failed'
      toast.error(msg)
    } finally {
      setTestingDb(false)
    }
  }

  const handleSaveShortUserId = async () => {
    const next = shortUserIdInput.trim()
    if (next && !/^[A-Za-z0-9_]{1,10}$/.test(next)) {
      toast.error('Runtime User ID must be 1–10 chars (letters, digits, underscore)')
      return
    }
    setSavingShortUserId(true)
    try {
      await updateShortUserId(next)
      await refreshUser()
      toast.success(next ? 'Runtime User ID saved' : 'Runtime User ID cleared')
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save Runtime User ID'
      toast.error(msg)
    } finally {
      setSavingShortUserId(false)
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error('All fields are required')
      return
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match')
      return
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    setChangingPassword(true)
    try {
      await changePassword(currentPassword, newPassword, confirmNewPassword)
      toast.success('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to change password'
      toast.error(msg)
    } finally {
      setChangingPassword(false)
    }
  }

  const updateSetting = <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    setSettings((prev) => {
      const updated = { ...prev, [key]: value }
      // Auto-save generation_mode immediately so other pages pick it up
      if (key === 'generation_mode') {
        saveSettings({ generation_mode: value as string })
          .then(() => {
            window.dispatchEvent(new CustomEvent('twasta:settings-changed'))
          })
          .catch(() => {})
      }
      // Auto-set application_database and default port when deployment_target changes
      if (key === 'deployment_target') {
        const autoDb = TARGET_TO_DB[value as string]
        if (autoDb) {
          updated.application_database = autoDb
          updated.db_port = DEFAULT_PORTS[autoDb] || ''
        }
      }
      return updated
    })
  }

  const updateScm = <K extends keyof ScmSettings>(key: K, value: ScmSettings[K]) => {
    setScmSettings((prev) => ({ ...prev, [key]: value }))
  }

  const currentModels = settings.llm_provider ? (MODELS_BY_PROVIDER[settings.llm_provider] || []) : []

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)',
      }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', animation: 'fadeIn 0.25s ease-out' }}>
      {/* Header with back button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.25rem' }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate(-1)}
          title="Back"
          style={{ padding: '4px 8px' }}
        >
          <span className="material-icons" style={{ fontSize: 21 }}>arrow_back</span>
        </button>
        <h1 style={{ fontSize: '1.5625rem', fontWeight: 600, margin: 0 }}>
          Settings
        </h1>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: '1px solid var(--color-border)',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={() => setActiveTab('general')}
          style={{
            padding: '8px 20px',
            fontSize: '0.9375rem',
            fontWeight: activeTab === 'general' ? 600 : 400,
            color: activeTab === 'general' ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'general' ? '2px solid var(--color-accent)' : '2px solid transparent',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span className="material-icons" style={{ fontSize: 17 }}>psychology</span>
          LLM
        </button>
        <button
          onClick={() => setActiveTab('scm')}
          style={{
            padding: '8px 20px',
            fontSize: '0.9375rem',
            fontWeight: activeTab === 'scm' ? 600 : 400,
            color: activeTab === 'scm' ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'scm' ? '2px solid var(--color-accent)' : '2px solid transparent',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span className="material-icons" style={{ fontSize: 17 }}>code</span>
          Source Code Control
        </button>
        <button
          onClick={() => setActiveTab('agent-access')}
          style={{
            padding: '8px 20px',
            fontSize: '0.9375rem',
            fontWeight: activeTab === 'agent-access' ? 600 : 400,
            color: activeTab === 'agent-access' ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'agent-access' ? '2px solid var(--color-accent)' : '2px solid transparent',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span className="material-icons" style={{ fontSize: 17 }}>terminal</span>
          Agent Access
        </button>
        <button
          onClick={() => setActiveTab('account')}
          style={{
            padding: '8px 20px',
            fontSize: '0.9375rem',
            fontWeight: activeTab === 'account' ? 600 : 400,
            color: activeTab === 'account' ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'account' ? '2px solid var(--color-accent)' : '2px solid transparent',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span className="material-icons" style={{ fontSize: 17 }}>person</span>
          Account
        </button>
      </div>

      {/* ---- LLM TAB (formerly General) ---- */}
      {activeTab === 'general' && (
        <LlmSettingsPanel />
      )}

      {/* ---- AGENT ACCESS TAB ---- */}
      {activeTab === 'agent-access' && (
        <AgentAccessPanel />
      )}

      {/* ---- DEPLOYMENT TAB ---- */}

      {/* ---- SOURCE CODE CONTROL TAB ---- */}
      {activeTab === 'scm' && (
        <>
          {/* SCM Enable/Disable */}
          <section style={{
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '1.5rem',
            marginBottom: '1.5rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
              <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>code</span>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Source Code Control</h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Enable toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9375rem' }}>
                <input
                  type="checkbox"
                  checked={scmSettings.scm_enabled}
                  onChange={(e) => updateScm('scm_enabled', e.target.checked)}
                />
                <span>Enable Source Code Control</span>
              </label>

              {scmSettings.scm_enabled && (
                <>
                  {/* Provider */}
                  <div>
                    <label htmlFor="scm-provider">SCM Provider</label>
                    <select
                      id="scm-provider"
                      value={scmSettings.scm_provider}
                      onChange={(e) => updateScm('scm_provider', e.target.value)}
                    >
                      <option value="github">GitHub</option>
                      <option value="gitlab">GitLab</option>
                    </select>
                  </div>

                  {/* Auto-create repo */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9375rem' }}>
                    <input
                      type="checkbox"
                      checked={scmSettings.auto_create_repo}
                      onChange={(e) => updateScm('auto_create_repo', e.target.checked)}
                    />
                    <span>Auto-create repository on draft save</span>
                  </label>
                </>
              )}
            </div>
          </section>

          {/* GitHub Settings */}
          {scmSettings.scm_enabled && scmSettings.scm_provider === 'github' && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
                <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>hub</span>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>GitHub Configuration</h2>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label htmlFor="gh-username">Username / Organization</label>
                  <input
                    id="gh-username"
                    type="text"
                    value={scmSettings.github_username}
                    onChange={(e) => updateScm('github_username', e.target.value)}
                    placeholder="Your GitHub username or organization"
                  />
                </div>

                <div>
                  <label htmlFor="gh-token">Personal Access Token</label>
                  <input
                    id="gh-token"
                    type="password"
                    value={scmSettings.github_token}
                    onChange={(e) => updateScm('github_token', e.target.value)}
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                    autoComplete="new-password"
                    data-form-type="other"
                    data-lpignore="true"
                  />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
                    Generate a token at GitHub &rarr; Settings &rarr; Developer settings &rarr; Personal access tokens
                  </span>
                </div>

                <div>
                  <label htmlFor="gh-branch">Default Branch</label>
                  <input
                    id="gh-branch"
                    type="text"
                    value={scmSettings.github_default_branch}
                    onChange={(e) => updateScm('github_default_branch', e.target.value)}
                    placeholder="main"
                  />
                </div>
              </div>
            </section>
          )}

          {/* GitLab Settings */}
          {scmSettings.scm_enabled && scmSettings.scm_provider === 'gitlab' && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
                <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>cloud_queue</span>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>GitLab Configuration</h2>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label htmlFor="gl-url">GitLab URL</label>
                  <input
                    id="gl-url"
                    type="url"
                    value={scmSettings.gitlab_url}
                    onChange={(e) => updateScm('gitlab_url', e.target.value)}
                    placeholder="https://gitlab.com"
                  />
                </div>

                <div>
                  <label htmlFor="gl-username">Username / Group</label>
                  <input
                    id="gl-username"
                    type="text"
                    value={scmSettings.gitlab_username}
                    onChange={(e) => updateScm('gitlab_username', e.target.value)}
                    placeholder="Your GitLab username or group"
                  />
                </div>

                <div>
                  <label htmlFor="gl-token">Personal Access Token</label>
                  <input
                    id="gl-token"
                    type="password"
                    value={scmSettings.gitlab_token}
                    onChange={(e) => updateScm('gitlab_token', e.target.value)}
                    placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
                    autoComplete="new-password"
                    data-form-type="other"
                    data-lpignore="true"
                  />
                </div>

                <div>
                  <label htmlFor="gl-branch">Default Branch</label>
                  <input
                    id="gl-branch"
                    type="text"
                    value={scmSettings.gitlab_default_branch}
                    onChange={(e) => updateScm('gitlab_default_branch', e.target.value)}
                    placeholder="main"
                  />
                </div>
              </div>
            </section>
          )}

          {/* SCM Actions.

              Save is ALWAYS rendered. It used to be gated on scm_enabled,
              which made turning source control OFF impossible: unchecking the
              box hid the only control that could persist the change, so
              reopening Settings read the still-enabled value back from the
              server and the checkbox appeared to tick itself again. Test
              Connection stays gated — there are no credentials on screen to
              test when source control is off. */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingBottom: '2rem' }}>
            {scmSettings.scm_enabled && (
              <button
                className="btn btn-secondary"
                onClick={handleTestScmConnection}
                disabled={testingScm}
              >
                {testingScm ? (
                  <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Testing...</>
                ) : (
                  <><span className="material-icons" style={{ fontSize: 17 }}>wifi_tethering</span> Test Connection</>
                )}
              </button>
            )}
            <button
              className="btn btn-primary btn-lg"
              onClick={handleSaveScm}
              disabled={savingScm}
            >
              {savingScm ? (
                <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Saving...</>
              ) : (
                <><span className="material-icons" style={{ fontSize: 19 }}>save</span> Save SCM Settings</>
              )}
            </button>
          </div>
        </>
      )}

      {/* ---- ACCOUNT TAB ---- */}
      {activeTab === 'account' && (
        <>
          <BillingProfileCard />

          {/* Profile Info */}
          <section style={{
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '1.5rem',
            marginBottom: '1.5rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
              <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>badge</span>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Profile</h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label>Name</label>
                <div style={{ fontWeight: 500, fontSize: '1rem', padding: '0.5rem 0' }}>
                  {user?.user_name || '-'}
                </div>
              </div>
              <div>
                <label>Email</label>
                <div style={{ fontWeight: 500, fontSize: '1rem', padding: '0.5rem 0' }}>
                  {user?.email || '-'}
                </div>
              </div>
              <div>
                <label>Role</label>
                <div style={{ padding: '0.5rem 0' }}>
                  <span className="badge badge-info">{roleDisplay || user?.role || '-'}</span>
                </div>
              </div>
              <div>
                <label>Organization</label>
                <div style={{ fontWeight: 500, fontSize: '1rem', padding: '0.5rem 0' }}>
                  {user?.organization || '-'}
                </div>
              </div>
            </div>

            {/* Runtime (10-char) User ID — stamped on ADD_USER / CHG_USER */}
            <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--color-border)' }}>
              <label htmlFor="short-user-id" style={{ display: 'block', marginBottom: 6 }}>
                Runtime User ID
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  id="short-user-id"
                  type="text"
                  value={shortUserIdInput}
                  onChange={(e) => setShortUserIdInput(e.target.value.slice(0, 10))}
                  placeholder={user?.user_id ? user.user_id.slice(-10) : '10-char ID'}
                  maxLength={10}
                  style={{ maxWidth: 220, fontFamily: 'var(--font-mono, monospace)' }}
                  autoComplete="off"
                  disabled={savingShortUserId}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSaveShortUserId}
                  disabled={savingShortUserId || shortUserIdInput === (user?.short_user_id || '')}
                >
                  {savingShortUserId ? (
                    <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving</>
                  ) : 'Save'}
                </button>
                {user?.effective_short_user_id && (
                  <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                    Effective:&nbsp;
                    <code style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                      {user.effective_short_user_id}
                    </code>
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: 6 }}>
                Up to 10 characters (letters, digits, underscore). Used by the runtime
                for <code>ADD_USER</code> / <code>CHG_USER</code> audit columns. Leave
                blank to use the last 10 characters of your account ID.
              </div>
            </div>
          </section>

          {/* Change Password */}
          <section style={{
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '1.5rem',
            marginBottom: '1.5rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
              <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>lock</span>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Change Password</h2>
            </div>

            <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleChangePassword() }} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 400 }}>
              {/* Hidden dummy field to absorb browser autofill */}
              <input type="text" name="prevent_autofill" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
              <div>
                <label htmlFor="current-password">Current Password</label>
                <input
                  id="current-password"
                  type="password"
                  name="current_pw"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  autoComplete="off"
                  data-form-type="other"
                  data-lpignore="true"
                />
              </div>
              <div>
                <label htmlFor="new-password">New Password</label>
                <input
                  id="new-password"
                  type="password"
                  name="new_pw"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  autoComplete="off"
                  data-form-type="other"
                  data-lpignore="true"
                />
              </div>
              <div>
                <label htmlFor="confirm-new-password">Confirm New Password</label>
                <input
                  id="confirm-new-password"
                  type="password"
                  name="confirm_pw"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="off"
                  data-form-type="other"
                  data-lpignore="true"
                />
              </div>
              <div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmNewPassword}
                >
                  {changingPassword ? (
                    <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Changing...</>
                  ) : (
                    <><span className="material-icons" style={{ fontSize: 17 }}>lock_reset</span> Change Password</>
                  )}
                </button>
              </div>
            </form>
          </section>

          <TwoFactorCard />
        </>
      )}
    </div>
  )
}
